import { DateFormat, formatDateTimeWithLocale } from '@frontend/common';
import { CommonMessage, useLocale } from '@frontend/lang';
import { StockExtended } from '@frontend/stock/types';
import { ExtendedVendingTransaction, TransactionReturnReason } from '@frontend/transaction/types';
import { UserBadge } from '@frontend/user/badge';
import { LuCalendar, LuUser } from 'react-icons/lu';
import { FormattedMessage } from 'react-intl';

import KioskList from '../../../components/kiosk-list.component';
import WizardScreenContainer from '../../components/wizard-screen-container/wizard-screen-container.component';
import { ProductCard } from '../browse-products-by-categories/product-card/product-card.component';
import useReviewReturns from './review-returns.controller';

const ID = 'review-returns';

const ReviewReturns = () => {
    const { locale } = useLocale();
    const viewProps = useReviewReturns();

    return (
        <WizardScreenContainer
            id={ID}
            loadingModal={{
                show: viewProps.isLoading,
                onClose: () => viewProps.setIsLoading(false)
            }}
            containerClassName='flex size-full flex-col overflow-x-hidden overflow-y-auto p-6 gap-3'>
            <div className='flex flex-col items-start justify-start gap-3 self-stretch'>
                <div className='text-foreground-default justify-end'>
                    <FormattedMessage
                        defaultMessage='Review products that were recently returned.'
                        id='kiosk.wizard.review-overview.instruction'
                        description='Instruction text on the lending review overview screen, prompting users to review recently returned products'
                    />
                </div>
                <KioskList<StockExtended | ExtendedVendingTransaction>
                    id='return-overview'
                    fetch={viewProps.fetchData}
                    rowElement={(obj) => (
                        <ProductCard
                            variant='attributes'
                            product_id={obj.product?.entity_id}
                            onClick={(product) => viewProps.onClick(product, obj)}
                            description={
                                <div className='flex flex-col items-start justify-start gap-1 self-stretch'>
                                    <div className='text-brand-muted inline-flex items-center justify-start gap-2 self-stretch text-base font-medium'>
                                        <LuCalendar className='size-5' />
                                        {formatDateTimeWithLocale(new Date(obj.creation_timestamp), locale, DateFormat.DD_MM_YYYY_HH_MM)}
                                    </div>
                                    {'user' in obj && obj.user ? (
                                        <div className='inline-flex items-center justify-start gap-2 self-stretch'>
                                            <LuUser className='size-5' />
                                            <UserBadge
                                                id={obj.user.entity_id}
                                                className='text-brand-muted justify-start text-base font-medium'
                                            />
                                        </div>
                                    ) : null}

                                    {'ean' in obj && obj.ean && (
                                        <div className='text-brand-muted inline-flex items-center justify-start gap-2 self-stretch text-base font-medium'>
                                            {CommonMessage.OBJECTS.PRODUCT.EAN} {obj.ean ?? '-'}
                                        </div>
                                    )}
                                    {'sku' in obj && obj.sku && (
                                        <div className='text-brand-muted inline-flex items-center justify-start gap-2 self-stretch text-base font-medium'>
                                            {CommonMessage.OBJECTS.PRODUCT.SKU} {obj.sku ?? '-'}
                                        </div>
                                    )}
                                    {'return_reason' in obj && obj.return_reason && (
                                        <div className='text-brand-muted inline-flex items-center justify-start gap-2 self-stretch text-base font-medium'>
                                            {CommonMessage.OBJECTS.TRANSACTION.RETURN_REASON}{' '}
                                            {obj.return_reason ? TransactionReturnReason.getByValue(obj.return_reason)?.displayName : '-'}
                                        </div>
                                    )}
                                </div>
                            }
                        />
                    )}
                    emptyText={
                        <FormattedMessage
                            id='kiosk.wizard.review-overview.no-products'
                            defaultMessage='No products to review'
                        />
                    }
                />
            </div>
        </WizardScreenContainer>
    );
};

export default ReviewReturns;
